import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip"

import { useEffect, useState } from "react"
import { createPortal } from "react-dom"

import { cn } from "@/lib/utils"

function TooltipProvider({
  delay = 0,
  ...props
}: TooltipPrimitive.Provider.Props) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delay={delay}
      {...props}
    />
  )
}

function Tooltip({ ...props }: TooltipPrimitive.Root.Props) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />
}

// Tooltips show at once and leave at once: no delay, no fade (Antonin, 13 Oct: "much faster apparition, no delay";
// rule "Tooltips on icon-only controls show at once, with no delay or fade"). A trigger can still pass its own.
function TooltipTrigger({ delay = 0, closeDelay = 0, ...props }: TooltipPrimitive.Trigger.Props) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" delay={delay} closeDelay={closeDelay} {...props} />
}

function TooltipContent({
  className,
  side = "top",
  sideOffset = 4,
  align = "center",
  alignOffset = 0,
  children,
  ...props
}: TooltipPrimitive.Popup.Props &
  Pick<
    TooltipPrimitive.Positioner.Props,
    "align" | "alignOffset" | "side" | "sideOffset"
  >) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        className="isolate z-50"
      >
        <TooltipPrimitive.Popup
          data-slot="tooltip-content"
          className={cn(
            "z-50 inline-flex w-fit max-w-xs origin-(--transform-origin) items-center gap-2 rounded-md bg-foreground px-3 py-2 text-xs text-background has-data-[slot=kbd]:pr-2 **:data-[slot=kbd]:relative **:data-[slot=kbd]:isolate **:data-[slot=kbd]:z-50 **:data-[slot=kbd]:rounded-sm",
            className
          )}
          {...props}
        >
          {children}
          <TooltipPrimitive.Arrow className="z-50 size-2 translate-y-[calc(-50%-2px)] rotate-45 rounded-[2px] bg-foreground fill-foreground data-[side=bottom]:top-1 data-[side=inline-end]:top-1/2! data-[side=inline-end]:-left-1 data-[side=inline-end]:-translate-y-1/2 data-[side=inline-start]:top-1/2! data-[side=inline-start]:-right-1 data-[side=inline-start]:-translate-y-1/2 data-[side=left]:top-1/2! data-[side=left]:-right-1 data-[side=left]:-translate-y-1/2 data-[side=right]:top-1/2! data-[side=right]:-left-1 data-[side=right]:-translate-y-1/2 data-[side=top]:-bottom-3" />
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  )
}

/**
 * IconButtonTooltips: the instant tooltip for every icon-only button that has no tooltip of its own (Antonin, 15 Oct:
 * "yes put tooltips"). Mount once. A button, role=button or link with an aria-label and no visible text shows its label
 * at once (no delay, no fade), 4 px above it (below it when there is no room), in the same look as TooltipContent.
 * It skips a trigger that already carries a Tooltip (data-slot="tooltip-trigger"), the rail (its own tip; its expand / collapse
 * toggle, `data-rail-toggle`, is the exception and gets this one) and anything marked data-no-tip. The native `title` attribute is never used for this.
 */
const ICON_BUTTON = 'button, [role="button"], a[href]'

function iconButtonLabel(target: EventTarget | null): { el: HTMLElement; label: string } | null {
  if (!(target instanceof Element)) return null
  const el = target.closest<HTMLElement>(ICON_BUTTON)
  if (!el) return null
  const label = el.getAttribute("aria-label")?.trim()
  if (!label) return null
  if (el.textContent?.trim()) return null // it shows its own words
  if (el.matches('[data-slot="tooltip-trigger"], [data-no-tip]') || (el.closest('[data-rail-tip], nav[aria-label="Main navigation"]') && !el.matches('[data-rail-toggle]'))) return null
  return { el, label }
}

function IconButtonTooltips() {
  const [tip, setTip] = useState<{ label: string; left: number; top: number; below: boolean } | null>(null)

  useEffect(() => {
    let current: HTMLElement | null = null
    const hide = () => { current = null; setTip(null) }
    const show = (target: EventTarget | null) => {
      const hit = iconButtonLabel(target)
      if (!hit) { if (current) hide(); return }
      if (hit.el === current) return
      current = hit.el
      const r = hit.el.getBoundingClientRect()
      const below = r.top < 40
      setTip({ label: hit.label, left: r.left + r.width / 2, top: below ? r.bottom + 4 : r.top - 4, below })
    }
    const over = (e: MouseEvent) => show(e.target)
    const focus = (e: FocusEvent) => { if ((e.target as Element | null)?.matches?.(":focus-visible")) show(e.target) }
    document.addEventListener("mouseover", over)
    document.addEventListener("focusin", focus)
    document.addEventListener("focusout", hide)
    document.addEventListener("mousedown", hide)
    document.addEventListener("scroll", hide, true)
    document.addEventListener("keydown", hide)
    return () => {
      document.removeEventListener("mouseover", over)
      document.removeEventListener("focusin", focus)
      document.removeEventListener("focusout", hide)
      document.removeEventListener("mousedown", hide)
      document.removeEventListener("scroll", hide, true)
      document.removeEventListener("keydown", hide)
    }
  }, [])

  if (!tip) return null
  return createPortal(
    <div
      role="tooltip"
      data-icon-button-tip
      className="pointer-events-none fixed z-[400] w-fit max-w-xs whitespace-nowrap rounded-md bg-foreground px-3 py-2 text-xs text-background"
      // Centred on the button, then pulled back inside the window (8 from each edge).
      ref={(el) => {
        if (!el) return
        const w = el.offsetWidth
        el.style.left = `${Math.min(Math.max(tip.left - w / 2, 8), window.innerWidth - w - 8)}px`
      }}
      style={{ top: tip.top, transform: tip.below ? undefined : "translateY(-100%)" }}
    >
      {tip.label}
    </div>,
    document.body,
  )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, IconButtonTooltips }
